import type { AssetManifest } from '@kenocyber/shared';
import {
  CANNON_CATALOG,
  cannonCountForLoadoutLevel,
  type InventoryInstanceWire,
  ITEM_LEVEL_CAPS,
  isCannonRarity,
  itemCategoryLabel,
  itemDisplayName,
  type LoadoutWire,
  SHIP_LEVEL_1_DAMAGE_BONUS_BPS,
  TRADE_BLOCK_MESSAGES,
  TRADE_STATUS_LABELS,
} from '@kenocyber/shared';
import { useState } from 'react';
import { itemArtUrl } from '../game/assets';
import { rarityLabel, rarityPaint, rarityStyle } from './rarity';

/**
 * Details of the selected item, and the only place equipping starts.
 *
 * Everything shown is computed from what the SERVER sent. The client does not
 * derive a bonus, a projectile count or a rarity of its own - it renders the
 * numbers the vault resolved, so what a player reads here is what the hunt is
 * actually configured with.
 */

interface InventoryDetailsProps {
  readonly item: InventoryInstanceWire | null;
  readonly loadout: LoadoutWire | null;
  readonly manifest: AssetManifest;
  readonly equipping: boolean;
  readonly error: string | null;
  readonly onEquip: (itemId: string) => void;
}

/**
 * The trade status this copy is in, as one badge.
 *
 * Comes from the SERVER's own verdict (`unavailableReason`), which the vault
 * derives with the same resolver the trade uses - so a card can never disagree
 * with what an offer will accept.
 */
function tradeStatusLabel(item: InventoryInstanceWire): string {
  return item.availableForTrade
    ? TRADE_STATUS_LABELS.tradable
    : TRADE_STATUS_LABELS[item.unavailableReason ?? 'unavailable'];
}

function Row({ label, value, testId }: { label: string; value: string; testId?: string }) {
  return (
    <div className="inv-detail-row">
      <span className="inv-detail-label">{label}</span>
      <span className="inv-detail-value" data-testid={testId}>
        {value}
      </span>
    </div>
  );
}

/**
 * O que os Corações incorporados valem, em percentagem, sem passar por `number`.
 *
 * ## Porque a divisão é feita à mão sobre uma string
 *
 * Porque a contagem não tem tecto: `numeric(40, 0)` guarda quarenta dígitos, e
 * `Number('9'.repeat(40)) / 100` devolveria `1e38` - um número que já perdeu
 * vinte e cinco dígitos do que o jogador pagou. Um jogador com dez elevado a
 * trinta corações tem direito a ver o número dele.
 *
 * Cada coração vale +1 %, exatamente (ADR 0050), portanto a percentagem É a
 * contagem: `+N%`. Não há divisão nenhuma a fazer, e é por isso que esta função
 * é tão curta - o comentário existe para explicar porque não se escreveu a
 * multiplicação por cem que alguém iria "corrigir" mais tarde.
 */
function primordialBonusLabel(count: string | null): string {
  /*
   * "de dano CAUSADO", e a palavra a mais é o ponto.
   *
   * "+7% de dano" não diz de que lado o número está: um jogador pode lê-lo como
   * dano que a nave leva. É o mesmo vocabulário do modal de incorporação, que
   * diz "o dano causado pela nave aumentará permanentemente em 1%".
   */
  return `+${count ?? '0'}% de dano causado`;
}

export function InventoryDetails({
  item,
  loadout,
  manifest,
  equipping,
  error,
  onEquip,
}: InventoryDetailsProps) {
  const [confirming, setConfirming] = useState(false);

  if (!item) {
    return (
      <aside className="inv-details" data-testid="inventory-details">
        <p className="inv-state">Selecione um item.</p>
      </aside>
    );
  }

  /* O mesmo resolvedor da célula e da carga de campo. Ver `InventoryPanel`. */
  const url = itemArtUrl(manifest, item.definitionId, item.level);
  const maxLevel = ITEM_LEVEL_CAPS[item.category];
  const equippable = item.category === 'ship' || item.category === 'cannon';
  // What is in the slot right now, so the confirmation can show both sides.
  const current = item.category === 'ship' ? loadout?.ship : loadout?.cannon;

  return (
    <aside className="inv-details" data-testid="inventory-details" style={rarityStyle(item.rarity)}>
      {/* Fixed header: art and identity, always in view. */}
      <header className="inv-details-head">
        <div
          className="inv-details-art"
          data-rarity={item.rarity}
          data-paint={rarityPaint(item.rarity)}
        >
          {url ? <img src={url} alt="" draggable={false} /> : <span aria-hidden="true">?</span>}
        </div>
        <div className="inv-details-title">
          {/*
            **O nome e a categoria canónicos** (gate V28I.BI.2).
            Eram o `definitionId` cru e a categoria em inglês, com um comentário
            a dizer que faltavam nomes. Eles existem: `display-names.ts` é o
            único sítio onde uma definição vira uma palavra, e responde por
            todas as categorias. Um id desconhecido devolve "Item desconhecido"
            em vez de `zztest_relic`.
          */}
          <h3 data-testid="inv-detail-name">{itemDisplayName(item.definitionId)}</h3>
          <span className="inv-details-sub" data-testid="inv-detail-rarity">
            {rarityLabel(item.rarity)}
          </span>
          <span className="inv-details-sub" data-testid="inv-detail-category">
            {itemCategoryLabel(item.definitionId)}
          </span>
        </div>
      </header>

      {/* Only this middle region ever scrolls. */}
      <div className="inv-details-body">
        <Row
          label="Nível"
          value={maxLevel === null ? String(item.level) : `${item.level} / ${maxLevel}`}
          testId="inv-detail-level"
        />

        {item.category === 'ship' ? (
          <>
            <Row
              label="Bônus de dano"
              // Basis points, as the server computed them: 100 bps = 1%.
              value={`+${(item.equipped && loadout ? loadout.ship.damageBonusBps : bonusFor(item)) / 100}%`}
              testId="inv-detail-ship-bonus"
            />
            {/*
             * Os Corações Primordiais incorporados NESTA cópia (gate V28F.B).
             *
             * Duas linhas e não uma: a contagem é o facto, e a percentagem é o
             * que ela vale. Mostrar só a percentagem esconderia quantos corações
             * o jogador realmente pagou; mostrar só a contagem obrigá-lo-ia a
             * fazer a conta de cabeça.
             *
             * Sempre visível numa nave, mesmo a zero. Uma linha que desaparece
             * quando o valor é zero faz o jogador perguntar-se se a nave tem a
             * propriedade ou se a interface se esqueceu dela - e zero é uma
             * resposta.
             */}
            <Row
              label="Corações Primordiais"
              value={item.primordialHeartCount ?? '0'}
              testId="inv-detail-hearts"
            />
            <Row
              label="Bônus Primordial"
              value={primordialBonusLabel(item.primordialHeartCount)}
              testId="inv-detail-heart-bonus"
            />
          </>
        ) : null}

        {item.category === 'cannon' ? (
          <>
            <Row
              label="Canhões"
              value={String(cannonCountForLoadoutLevel(item.level))}
              testId="inv-detail-cannon-count"
            />
            <Row
              label="Projéteis por canhão"
              value={String(projectilesFor(item))}
              testId="inv-detail-per-cannon"
            />
            <Row
              label="Projéteis por disparo"
              value={String(cannonCountForLoadoutLevel(item.level) * projectilesFor(item))}
              testId="inv-detail-per-volley"
            />
          </>
        ) : null}

        <Row
          label="Negociável"
          value={item.tradable ? 'Sim' : 'Não'}
          testId="inv-detail-tradable"
        />
        <Row label="Estado" value={tradeStatusLabel(item)} testId="inv-detail-unavailable" />
        {/* The action that would make it eligible, when there is one. A bound
            copy gets no advice, because there is nothing to try. */}
        {item.unavailableReason && item.unavailableReason !== 'unavailable' ? (
          <p className="inv-trade-hint" data-testid="inv-trade-hint">
            {TRADE_BLOCK_MESSAGES[item.unavailableReason]}
          </p>
        ) : null}
      </div>

      {/* Fixed action footer, for the same reason the artifacts panel has one:
          an action a player has to scroll to find is not available to them. */}
      <footer className="inv-details-actions" data-testid="inventory-actions">
        {error ? (
          <p className="inv-detail-error" role="alert" data-testid="inv-equip-error">
            {error}
          </p>
        ) : null}

        {item.equipped ? (
          <p className="inv-equipped-tag" data-testid="inv-equipped-tag">
            EQUIPADO
          </p>
        ) : equippable ? (
          confirming ? (
            <div className="inv-confirm" data-testid="inv-equip-confirm">
              <p>
                Trocar {current ? current.definitionId : 'nada'} por {item.definitionId}?
              </p>
              <div className="inv-confirm-pair">
                <span data-testid="inv-confirm-current">
                  {current ? `${current.definitionId} · nível ${current.level}` : '—'}
                </span>
                <span aria-hidden="true">→</span>
                <span data-testid="inv-confirm-next">
                  {item.definitionId} · {rarityLabel(item.rarity)} · nível {item.level}
                </span>
              </div>
              <div className="inv-confirm-actions">
                <button
                  type="button"
                  data-testid="inv-equip-confirm-yes"
                  // Disabled the moment it is pressed: a double click cannot
                  // become two requests, and the answer decides, not the click.
                  disabled={equipping}
                  onClick={() => {
                    setConfirming(false);
                    onEquip(item.itemId);
                  }}
                >
                  {equipping ? 'Equipando…' : 'Confirmar'}
                </button>
                <button
                  type="button"
                  data-testid="inv-equip-cancel"
                  onClick={() => setConfirming(false)}
                >
                  Cancelar
                </button>
              </div>
            </div>
          ) : (
            <button
              type="button"
              className="inv-equip"
              data-testid="inv-equip"
              disabled={equipping || item.locked}
              onClick={() => setConfirming(true)}
            >
              {equipping ? 'Equipando…' : 'Equipar'}
            </button>
          )
        ) : (
          <p className="inv-state" data-testid="inv-not-equippable">
            Sem slot disponível ainda.
          </p>
        )}
      </footer>
    </aside>
  );
}

/**
 * The ship bonus for an item that is NOT equipped, as a preview.
 *
 * The EQUIPPED one always comes from the server's resolved loadout. This is a
 * preview of what another ship would give, read from the same shared catalog
 * table the server resolves with - never a second formula written out here.
 * It informs a choice; it never decides a reward.
 */
function bonusFor(item: InventoryInstanceWire): number {
  return SHIP_LEVEL_1_DAMAGE_BONUS_BPS[item.rarity] * 2 ** (item.level - 1);
}

/** Projectiles per cannon, from the catalog. Cannon rarities only. */
function projectilesFor(item: InventoryInstanceWire): number {
  return isCannonRarity(item.rarity) ? CANNON_CATALOG[item.rarity].projectilesPerCannon : 1;
}
